import { useCallback } from "react";
import { buildSelection } from "@/features/betslip/builders/buildSelection";
import {
  isRacingEvent,
  type ToggleSelectionInput,
} from "@/features/betslip/builders/types";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useTokenContext } from "@/features/betslip/hooks/useTokenContext";
import { useDockActions } from "@/features/shell/hooks/useDock";
import { triggerHaptic } from "utilities/haptics";
import { telemetry } from "@/utilities/telemetry";

type TelemetryEventFields = {
  eventName: string;
  eventType: string;
  parentEventName: string;
  sport: string;
  sportCategory: string;
  status: string;
  streamUri: string;
  scheduledStartTime: string;
  activeMarketCount: number;
};

const extractTelemetryFields = (
  event: ToggleSelectionInput["event"],
): TelemetryEventFields => {
  const scheduledStartTime =
    event.scheduledStartTime instanceof Date
      ? event.scheduledStartTime.toISOString()
      : String(event.scheduledStartTime ?? "");

  if (isRacingEvent(event)) {
    return {
      eventName: event.eventName ?? "",
      eventType: event.eventType ?? "",
      parentEventName: "",
      sport: event.sport ?? "",
      sportCategory: "",
      status: event.status ?? "",
      streamUri: "",
      scheduledStartTime,
      activeMarketCount: 0,
    };
  }

  return {
    eventName: event.eventName ?? "",
    eventType: event.eventType ?? "",
    parentEventName: event.parentEventName ?? "",
    sport: event.sport ?? "",
    sportCategory: event.sportCategory ?? "",
    status: event.status ?? "",
    streamUri: event.streamUri ?? "",
    scheduledStartTime,
    activeMarketCount: event.activeMarketCount ?? 0,
  };
};

/**
 * Selection toggle. Pass raw building blocks (event/market/outcome/hub);
 * TypeScript enforces per-kind constraints via the discriminated input
 * union.
 */
export const useToggleSelection = () => {
  const tokenContext = useTokenContext();
  const { revealDock } = useDockActions();

  const toggle = useCallback(
    (input: ToggleSelectionInput) => {
      const selection = buildSelection(input);
      const store = useBetslipStore.getState();
      if (store.selections[selection.outcomeId]) {
        store.removeSelection(selection.outcomeId, tokenContext);
        triggerHaptic.click();
        return;
      }
      store.addSelection(selection, tokenContext);
      // Adding a selection pulls the user back to the betslip: the slip's
      // tab is shared, so a fresh add always lands on the active list.
      store.setTab("active");
      triggerHaptic.click();

      revealDock("betslip");

      telemetry.betAddedToSlip({
        ...extractTelemetryFields(input.event),
        eventId: selection.eventId,
        hub: selection.hub,
        tab: "",
        visible: true,
        // Conversion attribution needs a concrete origin on every event.
        originType: input.originType ?? "BETTING",
        originId: input.originId,
        ...(input.kind === "sem" && { builder: input.builder }),
      });
    },
    [tokenContext, revealDock],
  );

  return { toggle };
};
